import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, IconButton,
  Chip, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import PrintIcon from '@mui/icons-material/Print';
import api from '../../services/api.js';
import ImportSection from '../../components/ImportSection.jsx';
import { formatHebrewDate } from '../../utils/hebrewDate';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';

export default function ExamPeriodsList() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // create / edit state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '' });

  // delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);

  // deactivate all
  const [deactivateConfirm, setDeactivateConfirm] = useState(false);

  // backfill
  const [backfillTarget, setBackfillTarget] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillCount, setBackfillCount] = useState(null);
  const [backfillGradesTarget, setBackfillGradesTarget] = useState(null);
  const [backfillGradesLoading, setBackfillGradesLoading] = useState(false);
  const [backfillGradesStats, setBackfillGradesStats] = useState(null);

  // issuance tracking per period
  const [issuanceMap, setIssuanceMap] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/exam-periods');
      const list = data.data || [];
      setPeriods(list);

      // Fetch last issuance for each period
      const iMap = {};
      await Promise.all(list.map(async (p) => {
        try {
          const { data: last } = await api.get(`/exam-issuance/last?examPeriodId=${p._id}`);
          if (last) iMap[p._id] = last;
        } catch { /* ignore */ }
      }));
      setIssuanceMap(iMap);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditId(null);
    setForm({ name: '' });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditId(p._id);
    setForm({ name: p.name });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = { name: form.name };

      if (editId) {
        await api.put(`/exam-periods/${editId}`, payload);
        setSnackbar({ open: true, message: 'הזמן עודכן', severity: 'success' });
      } else {
        await api.post('/exam-periods', payload);
        setSnackbar({ open: true, message: 'הזמן נוצר', severity: 'success' });
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'שגיאה', severity: 'error' });
    }
  };

  const handleActivate = async (id) => {
    try {
      await api.post(`/exam-periods/${id}/activate`);
      setSnackbar({ open: true, message: 'הזמן הופעל', severity: 'success' });
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'שגיאה', severity: 'error' });
    }
  };

  const openBackfill = async (p) => {
    setBackfillTarget(p);
    setBackfillCount(null);
    try {
      const { data } = await api.post(`/exam-periods/${p._id}/backfill?dryRun=1`);
      setBackfillCount(data.count);
    } catch (err) {
      setSnackbar({ open: true, message: 'שגיאה בספירת רשומות', severity: 'error' });
      setBackfillTarget(null);
    }
  };

  const handleBackfill = async () => {
    setBackfillLoading(true);
    try {
      const { data } = await api.post(`/exam-periods/${backfillTarget._id}/backfill`);
      setSnackbar({ open: true, message: `שויכו ${data.updated} נרשמים למועד "${data.periodName}"`, severity: 'success' });
      setBackfillTarget(null);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'שגיאה', severity: 'error' });
    } finally {
      setBackfillLoading(false);
    }
  };

  const openBackfillGrades = async (p) => {
    setBackfillGradesTarget(p);
    setBackfillGradesStats(null);
    try {
      const { data } = await api.post(`/exam-periods/${p._id}/backfill-grades?dryRun=1`);
      setBackfillGradesStats({
        byRegistration: data.byRegistration ?? data.count ?? 0,
        orphanTotal: data.orphanTotal ?? 0,
      });
    } catch (err) {
      setSnackbar({ open: true, message: 'שגיאה בספירת ציונים', severity: 'error' });
      setBackfillGradesTarget(null);
    }
  };

  const handleBackfillGrades = async (assignAll = false) => {
    setBackfillGradesLoading(true);
    try {
      const url = assignAll
        ? `/exam-periods/${backfillGradesTarget._id}/backfill-grades?assignAll=1`
        : `/exam-periods/${backfillGradesTarget._id}/backfill-grades`;
      const { data } = await api.post(url);
      setSnackbar({
        open: true,
        message: `שויכו ${data.updated} ציונים למועד "${data.periodName}"`,
        severity: 'success',
      });
      setBackfillGradesTarget(null);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'שגיאה', severity: 'error' });
    } finally {
      setBackfillGradesLoading(false);
    }
  };

  const handleDeactivateAll = async () => {
    try {
      await api.post('/exam-periods/deactivate-all');
      setSnackbar({ open: true, message: 'הרישום הסתיים — אין מועד פעיל', severity: 'success' });
      setDeactivateConfirm(false);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'שגיאה', severity: 'error' });
      setDeactivateConfirm(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/exam-periods/${deleteTarget._id}`);
      setSnackbar({ open: true, message: 'הזמן נמחק', severity: 'success' });
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'שגיאה', severity: 'error' });
      setDeleteTarget(null);
    }
  };

  if (loading) return <Box><Typography>טוען...</Typography></Box>;

  return (
    <Box>
      <AdminPageHeader
        title="זמני מבחנים"
        sub={periods.find((p) => p.isActive) ? `פעיל: ${periods.find((p) => p.isActive).name}` : `${periods.length} זמנים`}
        primary={{ label: 'זמן חדש', onClick: openCreate }}
        actions={periods.some((p) => p.isActive) ? (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<StopCircleIcon />}
            onClick={() => setDeactivateConfirm(true)}
          >
            סיים רישום
          </Button>
        ) : null}
        legacy={(
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>זמני מבחנים</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {periods.some(p => p.isActive) && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<StopCircleIcon />}
                  onClick={() => setDeactivateConfirm(true)}
                >
                  סיים רישום
                </Button>
              )}
              <Button variant="contained" onClick={openCreate}>+ זמן חדש</Button>
            </Box>
          </Box>
        )}
      />

      {periods.length === 0 && (
        <Alert severity="info">אין זמני מבחנים. צרו זמן ראשון כדי להתחיל.</Alert>
      )}

      <Stack spacing={1.5}>
        {periods.map((p) => (
          <React.Fragment key={p._id}>
          <Paper
            sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: p.isActive ? '2px solid' : 'none', borderColor: 'primary.main' }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontWeight={700} fontSize={18}>{p.name}</Typography>
                {p.isActive && <Chip label="פעיל" color="primary" size="small" />}
                {issuanceMap[p._id] && (
                  <Chip
                    icon={<PrintIcon sx={{ fontSize: 14 }} />}
                    label={`הונפק ${formatHebrewDate(issuanceMap[p._id].issuedAt)}${issuanceMap[p._id].isPartial ? ' (חלקי)' : ''}`}
                    size="small"
                    sx={{
                      bgcolor: issuanceMap[p._id].isPartial ? 'var(--ck-warning-soft, #fff3e0)' : 'var(--ck-success-soft, #e8f5e9)',
                      color: issuanceMap[p._id].isPartial ? 'var(--ck-warning, #e65100)' : 'var(--ck-success, #2e7d32)',
                      fontWeight: 600,
                      fontSize: 11,
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {`מועד מספר: ${p.order + 1}`}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {!p.isActive && (
                <Button size="small" variant="outlined" color="primary" onClick={() => handleActivate(p._id)} startIcon={<CheckCircleIcon />}>
                  הפעל
                </Button>
              )}
              <Button size="small" variant="outlined" color="secondary" onClick={() => openBackfill(p)} startIcon={<MoveToInboxIcon />}>
                שייך נרשמים
              </Button>
              <Button size="small" variant="outlined" onClick={() => openBackfillGrades(p)} startIcon={<MoveToInboxIcon />}>
                שייך ציונים
              </Button>
              <IconButton size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" /></IconButton>
              <IconButton size="small" color="error" onClick={() => setDeleteTarget(p)} disabled={p.isActive}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>

          <ImportSection
            label={`ציונים — ${p.name}`}
            endpoint="/import/grades"
            templateEndpoint="/import/template/grades"
            description="עמודות: קוד אישי, קוד מבחן, ציון — המועד נשמר על הציון"
            extraFormData={{ periodId: p._id }}
            periodName={p.name}
          />

          <ImportSection
            label={`ציוני גמרא — ${p.name}`}
            endpoint="/import/gemara-grades"
            templateEndpoint="/import/template/gemara-grades"
            description="עמודות: קוד אישי + עמודות חוזרות של מסכת / דפים / ציון"
            extraFormData={{ periodId: p._id }}
            periodName={p.name}
          />
        </React.Fragment>
        ))}
      </Stack>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editId ? 'עריכת זמן' : 'זמן חדש'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="שם (למשל: ניסן פ״ו)"
              size="small"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>
            {editId ? 'שמור' : 'צור'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate all confirm */}
      <Dialog open={deactivateConfirm} onClose={() => setDeactivateConfirm(false)}>
        <DialogTitle>סיום רישום</DialogTitle>
        <DialogContent>
          <Typography>לסיים את תקופת הרישום? המועד הפעיל יכבה ולא יהיה ניתן להירשם דרך המערכת. הנרשמים הקיימים עדיין ישמרו.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateConfirm(false)}>ביטול</Button>
          <Button color="warning" variant="contained" onClick={handleDeactivateAll}>סיים רישום</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>מחיקת זמן</DialogTitle>
        <DialogContent>
          <Typography>למחוק את הזמן <strong>{deleteTarget?.name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>ביטול</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>מחק</Button>
        </DialogActions>
      </Dialog>

      {/* Backfill confirm */}
      <Dialog open={!!backfillTarget} onClose={() => !backfillLoading && setBackfillTarget(null)}>
        <DialogTitle>שיוך נרשמים ללא מועד</DialogTitle>
        <DialogContent>
          {backfillCount === null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={20} /><Typography>סופר רשומות...</Typography></Box>
          ) : (
            <Typography>
              נמצאו <strong>{backfillCount}</strong> נרשמים ללא שיוך למועד.<br />
              לשייך אותם למועד <strong>{backfillTarget?.name}</strong>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackfillTarget(null)} disabled={backfillLoading}>ביטול</Button>
          <Button
            variant="contained"
            onClick={handleBackfill}
            disabled={backfillLoading || backfillCount === null || backfillCount === 0}
          >
            {backfillLoading ? <CircularProgress size={18} /> : 'שייך'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backfill grades confirm */}
      <Dialog open={!!backfillGradesTarget} onClose={() => !backfillGradesLoading && setBackfillGradesTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>שיוך מועד לציונים — {backfillGradesTarget?.name}</DialogTitle>
        <DialogContent>
          {backfillGradesStats === null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={20} /><Typography>סופר ציונים...</Typography></Box>
          ) : (
            <Stack spacing={1.5}>
              <Typography>
                <strong>{backfillGradesStats.byRegistration}</strong> ציונים ללא מועד שיש להם הרשמה במועד זה.
              </Typography>
              <Typography color="text.secondary">
                סה״כ <strong>{backfillGradesStats.orphanTotal}</strong> ציוני הלכה במערכת ללא מועד.
              </Typography>
              {backfillGradesStats.orphanTotal === 0 && (
                <Alert severity="info">כל הציונים כבר משויכים למועד.</Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
          <Button onClick={() => setBackfillGradesTarget(null)} disabled={backfillGradesLoading}>ביטול</Button>
          <Button
            variant="outlined"
            onClick={() => handleBackfillGrades(false)}
            disabled={backfillGradesLoading || !backfillGradesStats || backfillGradesStats.byRegistration === 0}
          >
            {backfillGradesLoading ? <CircularProgress size={18} /> : 'לפי הרשמות'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => handleBackfillGrades(true)}
            disabled={backfillGradesLoading || !backfillGradesStats || backfillGradesStats.orphanTotal === 0}
          >
            {backfillGradesLoading ? <CircularProgress size={18} /> : 'כל הציונים ללא מועד'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
